export const BOT_NAME = "MIQ";
export const BOT_TAGLINE = "Your MarketIQ assistant";

export type BotIntent = {
  id: string;
  label: string;
  keywords: string[];
  answer: string;
  suggested?: string[];
};

export const INTENTS: BotIntent[] = [
  {
    id: "greeting",
    label: "Hi",
    keywords: ["hi", "hello", "hey", "good morning", "good evening", "namaste", "bro", "anna", "help chey"],
    answer:
      "Hey there! I'm MIQ, your in-app guide. Ask me anything about MarketIQ, AI content, SEO, social, or website publishing.",
    suggested: ["what_is_2io", "content_studio", "publish_website", "seo_hub"],
  },
  {
    id: "what_is_2io",
    label: "What is MarketIQ?",
    keywords: ["what is", "marketiq", "marketing iq", "miq", "about", "platform", "product"],
    answer:
      "MarketIQ is an AI-powered marketing operating system. You can run content, SEO, social, and reporting workflows from one workspace.",
    suggested: ["content_studio", "seo_hub", "integrations"],
  },
  {
    id: "content_studio",
    label: "How to create content?",
    keywords: [
      "content",
      "blog",
      "draft",
      "studio",
      "generate",
      "write",
      "blog create",
      "article generate",
      "content ela create",
      "blog ela rayali",
    ],
    answer:
      "Open AI Content Studio, choose your goal and tone, then generate drafts. Move draft -> review -> approved, and publish to social or 2io.live.",
    suggested: ["approve_draft", "publish_website", "social_publish"],
  },
  {
    id: "approve_draft",
    label: "How to approve draft?",
    keywords: [
      "approve",
      "review",
      "workflow",
      "draft review",
      "approval",
      "approve ela",
      "review nunchi approve",
      "draft ni approve cheyyali",
    ],
    answer:
      "In Draft Library, submit draft for review. Then open Workflow page, approve the draft item, and return to Draft Library to publish.",
    suggested: ["publish_website", "content_studio"],
  },
  {
    id: "publish_website",
    label: "Publish to website",
    keywords: [
      "publish website",
      "2io.live",
      "blog publish",
      "cover image",
      "service token",
      "image not loading",
      "website ki publish",
      "blog upload",
      "static website publish",
    ],
    answer:
      "For website publish, keep MARKETING_2IO_BLOG_SERVICE_TOKEN configured and ensure a cover image URL exists (draft meta.cover_image_url or MARKETING_2IO_BLOG_DEFAULT_COVER_URL). Then click Publish to 2io.live in Draft Library.",
    suggested: ["content_studio", "integrations"],
  },
  {
    id: "seo_hub",
    label: "SEO Hub sync",
    keywords: ["seo", "gsc", "ga4", "pagespeed", "hub sync", "keyword", "seo sync", "keyword research", "rank"],
    answer:
      "Use SEO Automation to run keyword research, audits, and hub sync. If sync is slow, retries and longer timeouts are supported in the gateway and frontend.",
    suggested: ["integrations", "analytics"],
  },
  {
    id: "social_publish",
    label: "Social publishing",
    keywords: [
      "social",
      "facebook",
      "instagram",
      "linkedin",
      "publish post",
      "meta",
      "page id",
      "social lo post",
      "fb publish",
    ],
    answer:
      "Create or approve content, then use Social Media / Draft Library promotion actions. Live publishing requires provider OAuth and account/page configuration.",
    suggested: ["integrations", "analytics"],
  },
  {
    id: "integrations",
    label: "Integrations setup",
    keywords: [
      "integration",
      "oauth",
      "connect",
      "google",
      "meta",
      "linkedin",
      "redirect uri",
      "invalid scopes",
      "integration setup",
    ],
    answer:
      "Open Integrations to connect providers. For OAuth issues, confirm redirect URI, scopes, and client credentials match provider app settings.",
    suggested: ["seo_hub", "social_publish"],
  },
  {
    id: "analytics",
    label: "Analytics and insights",
    keywords: ["analytics", "insights", "bi", "dashboard", "kpi", "report", "summary", "business intelligence"],
    answer:
      "Business Intelligence provides traffic/search insights, opportunities, summaries, and AI-assisted analysis to guide next actions.",
    suggested: ["seo_hub", "content_studio"],
  },
  {
    id: "draft_delete",
    label: "Delete draft",
    keywords: ["delete draft", "remove draft", "draft delete", "library delete", "draft ni delete"],
    answer:
      "Go to Draft Library, select the draft, and click Delete. It is soft-deleted from the library and removed from listing immediately after refresh.",
    suggested: ["content_studio", "approve_draft"],
  },
  {
    id: "publish_errors",
    label: "Publish error help",
    keywords: [
      "401",
      "400",
      "method not allowed",
      "unauthorized",
      "cover image required",
      "service token not set",
      "publish error",
      "not publishing",
    ],
    answer:
      "For publish errors, verify three things: 1) MARKETING_2IO_BLOG_SERVICE_TOKEN is set in the backend .env and loaded in marketing-service, 2) cover image URL exists (draft meta or MARKETING_2IO_BLOG_DEFAULT_COVER_URL), 3) static-site SERVICE_TOKEN matches exactly.",
    suggested: ["publish_website", "integrations"],
  },
];

const FALLBACK_SUGGESTED = ["content_studio", "approve_draft", "publish_website", "publish_errors"];

function score(query: string, intent: BotIntent): number {
  const q = query.toLowerCase();
  let s = 0;
  for (const kw of intent.keywords) {
    const k = kw.toLowerCase();
    if (q === k) s += 100;
    else if (q.includes(` ${k} `) || q.startsWith(`${k} `) || q.endsWith(` ${k}`)) s += 5 + k.length;
    else if (q.includes(k)) s += 1 + k.length;
  }
  return s;
}

export function findIntent(query: string): BotIntent | null {
  if (!query) return null;
  const ranked = INTENTS.map((i) => ({ intent: i, s: score(query, i) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s);
  return ranked[0]?.intent || null;
}

export function fallbackResponse(): { text: string; suggestedIds: string[] } {
  return {
    text: "I didn't fully catch that. Try one of these quick options, or rephrase your question:",
    suggestedIds: FALLBACK_SUGGESTED,
  };
}

const TECHNICAL_SYNCCBOT_REPLY = [
  /GEMINI_API_KEY/i,
  /OPENAI_API_KEY/i,
  /ai-service container/i,
  /Gemini request failed/i,
  /OpenAI request failed/i,
  /check.*ai-service.*logs/i,
];

export function normalizeSynccbotReply(
  reply: unknown,
  source: unknown,
): { reply: string; source: string } {
  const text = String(reply || "").trim();
  const src = String(source || "");
  if (!text) return { reply: "", source: src };
  const isTechnical = TECHNICAL_SYNCCBOT_REPLY.some((pattern) => pattern.test(text));
  if (!isTechnical) return { reply: text, source: src };
  return {
    reply: "I couldn't access that information right now. Please try again in a moment.",
    source: "fallback",
  };
}

export function intentById(id: string): BotIntent | null {
  return INTENTS.find((i) => i.id === id) || null;
}
